import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';
import type { AccessLevel, LiberationType } from '@/repos/enrollmentRepo';

export interface CreateModuleEnrollmentData {
  user_id: string;
  module_id: string;
  course_id: string;
  tenant_id?: string;
  liberation_type?: LiberationType;
  access_level?: AccessLevel;
  granted_by?: string;
}

export interface ModuleEnrollmentFilters {
  user_id?: string;
  course_id?: string;
  module_id?: string;
  liberation_type?: LiberationType;
  access_level?: AccessLevel;
  tenant_id?: string;
  page?: number;
  limit?: number;
}

export async function createModuleEnrollment(data: CreateModuleEnrollmentData): Promise<any> {
  const tenantId = data.tenant_id || (await getTenantId());
  const now = new Date().toISOString();

  const insertData: Record<string, unknown> = {
    user_id: data.user_id,
    module_id: data.module_id,
    course_id: data.course_id,
    tenant_id: tenantId,
    access_level: data.access_level || 'active',
    started_at: now,
  };

  if (data.liberation_type) insertData.liberation_type = data.liberation_type;
  if (data.granted_by) {
    insertData.granted_by = data.granted_by;
    insertData.granted_at = now;
  }

  const { data: enrollment, error } = await supabase.rpc('major_high_manage_academic_enrollment_v1', {
    p_operation: 'ASSIGN_STUDENT_TO_MODULE', p_user_id: data.user_id,
    p_course_id: data.course_id, p_module_id: data.module_id,
  });

  if (error) throw error;
  return enrollment;
}

export async function updateModuleEnrollment(
  enrollmentId: string,
  data: { access_level?: AccessLevel }
): Promise<any> {
  const updateData: Record<string, unknown> = {};
  if (data.access_level !== undefined) updateData.access_level = data.access_level;

  const { data: enrollment, error } = await supabase
    .from('module_enrollments')
    .update(updateData)
    .eq('id', enrollmentId)
    .select()
    .single();

  if (error) throw error;
  return enrollment;
}

export async function cancelModuleEnrollment(enrollmentId: string): Promise<void> {
  const { data: row, error: lookupError } = await supabase.from('module_enrollments').select('user_id,course_id,module_id').eq('id', enrollmentId).single();
  if (lookupError) throw lookupError;
  const { error } = await supabase.rpc('major_high_manage_academic_enrollment_v1', {
    p_operation: 'REMOVE_STUDENT_FROM_MODULE', p_user_id: row.user_id,
    p_course_id: row.course_id, p_module_id: row.module_id,
  });

  if (error) throw error;
}

export async function isUserEnrolledInModule(userId: string, moduleId: string): Promise<boolean> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('module_enrollments')
      .select('id')
      .eq('user_id', userId)
      .eq('module_id', moduleId)
      .eq('tenant_id', tenantId)
      .in('access_level', ['active', 'trial'])
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('❌ Erro ao verificar matrícula de módulo:', error);
    return false;
  }
}

export async function getAccessibleModuleIds(userId: string, courseId: string): Promise<string[]> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('module_enrollments')
      .select('module_id')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('tenant_id', tenantId)
      .in('access_level', ['active', 'trial']);

    if (error) throw error;
    return (data || []).map((row) => String(row.module_id));
  } catch (error) {
    console.error('❌ Erro ao listar módulos acessíveis:', error);
    return [];
  }
}

export async function getModuleEnrollmentsForAdmin(
  filters: ModuleEnrollmentFilters = {}
): Promise<{ enrollments: any[]; total: number }> {
  const tenantId = filters.tenant_id || (await getTenantId());
  const page = filters.page || 1;
  const limit = filters.limit || 50;
  const offset = (page - 1) * limit;

  let query = supabase
    .from('module_enrollments')
    .select(
      `
        *,
        users!module_enrollments_user_id_fkey (
          id,
          name,
          email
        ),
        courses (
          id,
          title,
          slug,
          cover
        ),
        modules (
          id,
          title,
          slug
        )
      `,
      { count: 'exact' }
    )
    .eq('tenant_id', tenantId)
    .neq('access_level', 'expired');

  if (filters.user_id) query = query.eq('user_id', filters.user_id);
  if (filters.course_id) query = query.eq('course_id', filters.course_id);
  if (filters.module_id) query = query.eq('module_id', filters.module_id);
  if (filters.liberation_type) query = query.eq('liberation_type', filters.liberation_type);
  if (filters.access_level) query = query.eq('access_level', filters.access_level);

  const { data, error, count } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;

  return {
    enrollments: data || [],
    total: count || 0,
  };
}

/** Linha normalizada para a tabela admin unificada */
export type AdminEnrollmentRow = {
  id: string;
  scopeType: 'course' | 'module';
  user_id: string;
  course_id: string;
  module_id?: string;
  access_level: string;
  liberation_type?: string;
  created_at?: string;
  granted_at?: string;
  users?: { id: string; name?: string; email?: string };
  courses?: { id: string; title?: string; slug?: string; cover?: string };
  modules?: { id: string; title?: string; slug?: string };
};

export function normalizeCourseEnrollment(row: any): AdminEnrollmentRow {
  return {
    id: row.id,
    scopeType: 'course',
    user_id: row.user_id,
    course_id: row.course_id,
    access_level: row.access_level,
    liberation_type: row.liberation_type,
    created_at: row.created_at,
    granted_at: row.granted_at,
    users: row.users,
    courses: row.courses,
  };
}

export function normalizeModuleEnrollment(row: any): AdminEnrollmentRow {
  return {
    id: row.id,
    scopeType: 'module',
    user_id: row.user_id,
    course_id: row.course_id,
    module_id: row.module_id,
    access_level: row.access_level,
    liberation_type: row.liberation_type,
    created_at: row.created_at,
    granted_at: row.granted_at,
    users: row.users,
    courses: row.courses,
    modules: row.modules,
  };
}
